import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import { marked } from "marked";
import html2pdf from "html2pdf.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Numbering,
  LevelFormat,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ShadingType,
} from "docx";
import { saveAs } from "file-saver";
import SafeIcon from "../common/SafeIcon";
import { format as formatDate } from "date-fns";
import { th } from "date-fns/locale";

const {
  FiX,
  FiSend,
  FiEye,
  FiEdit,
  FiAlertCircle,
  FiDownload,
  FiCheckCircle,
  FiLoader,
} = FiIcons;

const EmailSharingModal = ({
  isOpen,
  onClose,
  content,
  meetingDetails,
  onSend,
}) => {
  const [includeDetails, setIncludeDetails] = useState(true);
  const [emailData, setEmailData] = useState({
    to: "",
    cc: "",
    subject: "",
    message: "",
  });
  const [editableContent, setEditableContent] = useState("");
  const [sendingStatus, setSendingStatus] = useState({
    sending: false,
    success: false,
    error: null,
  });
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const pdfPreviewRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setEditableContent(content || "");
      setEmailData({
        to: "",
        cc: "",
        message: "",
        subject: meetingDetails?.title
          ? `สรุปการประชุม: ${meetingDetails.title}`
          : "สรุปการประชุม",
      });
      setSendingStatus({ sending: false, success: false, error: null });
      setIncludeDetails(true);
      setIsPreviewing(false);
      setIsGenerating(false);
    }
  }, [isOpen, content, meetingDetails]);

  const hasMeetingDetails =
    meetingDetails &&
    Object.values(meetingDetails).some((val) => val !== "" && val !== null);

  const formatMeetingDetailsForText = () => {
    if (!hasMeetingDetails) return "";
    let details = "--- รายละเอียดการประชุม ---\n";
    if (meetingDetails.title) details += `หัวข้อ: ${meetingDetails.title}\n`;
    if (meetingDetails.date) {
      const formattedDate = formatDate(
        new Date(meetingDetails.date),
        "d MMMM yyyy",
        { locale: th }
      );
      details += `วันที่: ${formattedDate}\n`;
    }
    if (meetingDetails.startTime || meetingDetails.endTime) {
      details += `เวลา: ${meetingDetails.startTime || ""}${
        meetingDetails.startTime && meetingDetails.endTime ? " - " : ""
      }${meetingDetails.endTime || ""}\n`;
    }
    if (meetingDetails.location)
      details += `สถานที่: ${meetingDetails.location}\n`;
    if (meetingDetails.participants)
      details += `ผู้เข้าร่วม: ${meetingDetails.participants}\n`;
    if (meetingDetails.agenda)
      details += `\nวาระการประชุม:\n${meetingDetails.agenda}\n`;
    if (meetingDetails.notes)
      details += `\nบันทึกเพิ่มเติม:\n${meetingDetails.notes}\n`;
    return details;
  };

  const handleDownloadPdf = () => {
    const element = document.getElementById("pdf-render-area");
    if (!element) return;
    setIsGenerating(true);
    const safeSubject = emailData.subject
      .replace(/[^a-z0-9-_\u0E00-\u0E7F]/gi, "_")
      .substring(0, 50);
    const opt = {
      margin: 0.5,
      filename: `สรุป-${safeSubject || "document"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };
    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .then(() => setIsGenerating(false))
      .catch((err) => {
        console.error(err);
        setIsGenerating(false);
      });
  };

  const handleDownloadDocx = async () => {
    let markdownContent = editableContent || "";
    if (includeDetails && hasMeetingDetails) {
      markdownContent += `\n\n${formatMeetingDetailsForText()}`;
    }
    if (!markdownContent) return;
    setIsGenerating(true);
    try {
      const createTextRunsFromTokens = (
        tokens,
        options = {},
        stripOtherHtml = false
      ) => {
        const textRuns = [];
        tokens.forEach((token) => {
          if (token.type === "strong") {
            textRuns.push(
              ...createTextRunsFromTokens(
                token.tokens,
                { ...options, bold: true },
                stripOtherHtml
              )
            );
          } else if (token.type === "em") {
            textRuns.push(
              ...createTextRunsFromTokens(
                token.tokens,
                { ...options, italic: true },
                stripOtherHtml
              )
            );
          } else if (token.type === 'br') {
            textRuns.push(new TextRun({ break: 1 }));
          } else if (token.text) {
            // Handle text content, splitting by <br> tags
            const textToProcess = token.text.replace(/&nbsp;/g, " ");
            const parts = textToProcess.split(/<br\s*\/?>/i);
            
            parts.forEach((part, index) => {
              let finalPart = part;
              if (stripOtherHtml) {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = finalPart;
                finalPart = tempDiv.textContent || tempDiv.innerText || "";
              }

              if (finalPart) {
                textRuns.push(new TextRun({ text: finalPart, ...options }));
              }

              if (index < parts.length - 1) {
                textRuns.push(new TextRun({ break: 1 }));
              }
            });
          }
        });
        return textRuns;
      };

      const tokens = marked.lexer(markdownContent);
      const children = [];

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "ถึง: ", bold: true }),
            new TextRun(emailData.to || ""),
          ],
        })
      );
      if (emailData.cc)
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "สำเนาถึง: ", bold: true }),
              new TextRun(emailData.cc),
            ],
          })
        );
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "หัวข้อ: ", bold: true }),
            new TextRun(emailData.subject || ""),
          ],
          spacing: { after: 240 },
        })
      );
      children.push(
        new Paragraph({
          border: {
            bottom: { color: "auto", space: 1, value: "single", size: 6 },
          },
          spacing: { after: 240 },
        })
      );
      if (emailData.message) {
        emailData.message
          .split("\n")
          .forEach((line) => children.push(new Paragraph(line)));
        children.push(new Paragraph(""));
      }

      const numbering = {
        config: [
          {
            reference: "my-bullet-points",
            levels: [
              {
                level: 0,
                format: LevelFormat.BULLET,
                text: "\u2022",
                alignment: AlignmentType.LEFT,
                style: { paragraph: { indent: { left: 720, hanging: 360 } } },
              },
            ],
          },
        ],
      };

      tokens.forEach((token) => {
        switch (token.type) {
          case "heading":
            children.push(
              new Paragraph({
                children: createTextRunsFromTokens(token.tokens),
                heading: HeadingLevel[`HEADING_${token.depth}`],
                spacing: { after: 200 },
              })
            );
            break;
          case "paragraph":
            children.push(
              new Paragraph({
                children: createTextRunsFromTokens(token.tokens),
                spacing: { after: 120 },
              })
            );
            break;
          case "table":
            const headerRows = [
              new TableRow({
                children: token.header.map(
                  (cell) =>
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: createTextRunsFromTokens(
                            cell.tokens,
                            {
                              bold: true,
                            },
                            true
                          ),
                          alignment: AlignmentType.CENTER,
                        }),
                      ],
                      shading: {
                        fill: "EFEFEF",
                        type: ShadingType.CLEAR,
                        color: "auto",
                      },
                      width: {
                        size: 100 / token.header.length,
                        type: WidthType.PERCENTAGE,
                      },
                    })
                ),
              }),
            ];
            const bodyRows = token.rows.map(
              (row) =>
                new TableRow({
                  children: row.map(
                    (cell) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: createTextRunsFromTokens(
                              cell.tokens,
                              {},
                              true
                            ),
                          }),
                        ],
                      })
                  ),
                })
            );
            children.push(
              new Table({
                rows: [...headerRows, ...bodyRows],
                width: { size: 100, type: WidthType.PERCENTAGE },
              })
            );
            children.push(new Paragraph(""));
            break;
          case "list":
            token.items.forEach((item) => {
              const p = item.tokens[0];
              children.push(
                new Paragraph({
                  children: createTextRunsFromTokens(p.tokens),
                  numbering: { reference: "my-bullet-points", level: 0 },
                })
              );
            });
            children.push(new Paragraph(""));
            break;
          case "space":
            children.push(new Paragraph(""));
            break;
          default:
            if (token.raw) children.push(new Paragraph(token.raw));
            break;
        }
      });

      const doc = new Document({
        numbering,
        sections: [{ children }],
        styles: {
          default: { document: { run: { font: "Sarabun", size: 24 } } },
        },
      });
      const blob = await Packer.toBlob(doc);
      const safeSubject = emailData.subject
        .replace(/[^a-z0-9-_\u0E00-\u0E7F]/gi, "_")
        .substring(0, 50);
      saveAs(blob, `สรุป-${safeSubject || "document"}.docx`);
    } catch (error) {
      console.error("Error generating DOCX:", error);
      alert("เกิดข้อผิดพลาดในการสร้าง DOCX: " + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChange = (e) =>
    setEmailData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendingStatus({ sending: true, success: false, error: null });
    const markdownContent =
      editableContent +
      (includeDetails && hasMeetingDetails
        ? `\n\n${formatMeetingDetailsForText()}`
        : "");

    marked.setOptions({
      gfm: true, // <-- เปิดใช้งาน GitHub Flavored Markdown (สำคัญที่สุดสำหรับตาราง)
      breaks: true, // <-- ทำให้การขึ้นบรรทัดใหม่ธรรมดา (Enter) ทำงาน
    });

    const htmlContent = marked.parse(markdownContent);
    // const htmlContent = marked(markdownContent, { breaks: true, gfm: true });
    try {
      await onSend({ ...emailData, htmlContent });
      setSendingStatus({ sending: false, success: true, error: null });
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      setSendingStatus({
        sending: false,
        success: false,
        error: "เกิดข้อผิดพลาดในการส่งอีเมล",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
        >
          {isPreviewing ? (
            // --- หน้า Preview ---
            <>
              {/* --- Header หน้า Preview (ออกแบบใหม่) --- */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-green-800 flex items-center gap-2">
                  <SafeIcon icon={FiEye} />
                  ตัวอย่างก่อนส่ง
                </h2>
                <button
                  onClick={() => setIsPreviewing(false)}
                  className="p-1.5 rounded-full text-slate-500 hover:text-green-600 hover:bg-green-100 transition-colors"
                >
                  <FiX size={22} />
                </button>
              </div>
              <div
                id="pdf-render-area"
                className="p-4 overflow-y-auto bg-white flex-1"
              >
                {/* 
    เราจะสร้าง div ครอบอีกชั้นสำหรับ prose theme 
    เพื่อให้องค์ประกอบที่ไม่ใช่ prose (เช่น Header) ไม่ถูกจัดสไตล์ไปด้วย 
  */}
                <div className="prose prose-sm prose-yungyoen max-w-none">
                  {/* 1. Header ของอีเมล (อยู่ "นอก" การจัดสไตล์ของ prose) */}
                  <div className="not-prose mb-4 text-sm space-y-1">
                    {emailData.to && (
                      <div>
                        <strong className="text-slate-500 font-medium">
                          ถึง:
                        </strong>{" "}
                        <span className="text-slate-800">{emailData.to}</span>
                      </div>
                    )}
                    {emailData.cc && (
                      <div>
                        <strong className="text-slate-500 font-medium">
                          สำเนาถึง:
                        </strong>{" "}
                        <span className="text-slate-800">{emailData.cc}</span>
                      </div>
                    )}
                    <div>
                      <strong className="text-slate-500 font-medium">
                        หัวข้อ:
                      </strong>{" "}
                      <span className="text-slate-800 font-semibold">
                        {emailData.subject}
                      </span>
                    </div>
                  </div>

                  {/* 2. เส้นคั่น */}
                  <hr className="my-4" />

                  {/* 3. ข้อความเพิ่มเติม */}
                  {emailData.message && (
                    <p className="whitespace-pre-wrap">{emailData.message}</p>
                  )}

                  {/* 4. เนื้อหาหลัก (ใช้ ReactMarkdown) */}
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {editableContent +
                      (includeDetails && hasMeetingDetails
                        ? `\n\n${formatMeetingDetailsForText()}`
                        : "")}
                  </ReactMarkdown>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setIsPreviewing(false)}
                  disabled={sendingStatus.sending || isGenerating}
                  className="px-4 py-2 bg-white text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-100 font-semibold flex items-center gap-1.5 disabled:opacity-60"
                >
                  <SafeIcon icon={FiEdit} /> กลับไปแก้ไข
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadDocx}
                    disabled={sendingStatus.sending || isGenerating}
                    className="px-3 py-2 bg-white text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-100 font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {isGenerating ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiDownload />
                    )}{" "}
                    <span>DOCX</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={sendingStatus.sending || isGenerating}
                    className="px-3 py-2 bg-white text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-100 font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {isGenerating ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiDownload />
                    )}{" "}
                    <span>PDF</span>
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                      !emailData.to ||
                      sendingStatus.sending ||
                      sendingStatus.success ||
                      isGenerating
                    }
                    className={`px-4 py-2 rounded-lg font-semibold flex items-center justify-center min-w-[120px] transition-colors shadow-sm disabled:opacity-60 ${
                      sendingStatus.success
                        ? "bg-green-600 text-white"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {sendingStatus.sending ? (
                      <FiLoader className="animate-spin" />
                    ) : sendingStatus.success ? (
                      <div className="flex items-center gap-1.5">
                        <FiCheckCircle /> <span>ส่งแล้ว</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiSend} />{" "}
                        <span>
                          {!emailData.to ? "กรุณากรอกอีเมล" : "ส่งอีเมล"}
                        </span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            // --- หน้า Edit ---
            <>
              {/* --- Header หน้า Edit (ออกแบบใหม่) --- */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-green-800 flex items-center gap-2">
                  <SafeIcon icon={FiEdit} /> {/* <-- ไอคอนใหม่ */}
                  แก้ไขและเตรียมส่ง
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full text-slate-500 hover:text-green-600 hover:bg-green-100 transition-colors"
                >
                  <FiX size={22} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <form id="emailForm" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="to"
                        className="block text-sm font-medium text-slate-600 mb-1"
                      >
                        ถึง (To)
                      </label>
                      <input
                        type="email"
                        id="to"
                        name="to"
                        value={emailData.to}
                        onChange={handleChange}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="cc"
                        className="block text-sm font-medium text-slate-600 mb-1"
                      >
                        สำเนาถึง (CC)
                      </label>
                      <input
                        type="text"
                        id="cc"
                        name="cc"
                        value={emailData.cc}
                        onChange={handleChange}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                        placeholder="คั่นด้วยจุลภาค (,)"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-slate-600 mb-1"
                    >
                      หัวข้อ (Subject)
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={emailData.subject}
                      onChange={handleChange}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-slate-600 mb-1"
                    >
                      ข้อความเพิ่มเติม
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={emailData.message}
                      onChange={handleChange}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      rows="2"
                      placeholder="เพิ่มข้อความส่วนตัวที่นี่..."
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="editableContent"
                      className="block text-sm font-medium text-slate-600 mb-1"
                    >
                      เนื้อหาหลัก (แก้ไขได้)
                    </label>
                    <textarea
                      id="editableContent"
                      value={editableContent}
                      onChange={(e) => setEditableContent(e.target.value)}
                      className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 min-h-[150px] text-sm"
                    />
                  </div>
                  <div className="border rounded-lg p-3 space-y-2 bg-white">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="includeDetails"
                        checked={includeDetails}
                        onChange={(e) => setIncludeDetails(e.target.checked)}
                        className="h-4 w-4 text-green-600 border-slate-300 rounded focus:ring-green-500"
                        disabled={!hasMeetingDetails}
                      />
                      <label
                        htmlFor="includeDetails"
                        className={`text-sm font-medium ${
                          !hasMeetingDetails
                            ? "text-slate-400"
                            : "text-slate-800"
                        }`}
                      >
                        แนบรายละเอียดการประชุม{" "}
                        {!hasMeetingDetails && "(ไม่มีข้อมูล)"}
                      </label>
                    </div>
                    {includeDetails && hasMeetingDetails && (
                      <pre className="text-xs text-slate-500 bg-slate-50 p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap font-sans">
                        {formatMeetingDetailsForText()}
                      </pre>
                    )}
                  </div>
                </form>
                {sendingStatus.error && (
                  <div className="p-3 mt-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2">
                    <SafeIcon icon={FiAlertCircle} />
                    <p className="text-sm">{sendingStatus.error}</p>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  onClick={() => setIsPreviewing(true)}
                  /* disabled={!emailData.to} */
                  className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1.5 shadow-sm disabled:opacity-60"
                >
                  <SafeIcon icon={FiEye} /> ดูตัวอย่างและส่งหรือดาวน์โหลด
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EmailSharingModal;
